import { useState, useMemo, useRef, useCallback, useEffect, HTMLAttributes } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  body1Bold,
  body1Medium,
  body2,
  caption,
  colors,
  fonts,
  fontSizes,
  fontWeights,
  letterSpacing,
  spacing,
  subtitle2,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';

/**
 * Delay (ms) between the end of the line animation and the start of the next ticker transition.
 */
const IDLE_DELAY_MS = 200;

/**
 * Duration (ms) of the chart line draw animation.
 */
const LINE_ANIMATION_MS = 3600;

/**
 * Duration (ms) of the ticker vertical transition.
 */
const TICKER_TRANSITION_MS = 400;

/**
 * SVG viewBox width for the chart line.
 */
const CHART_VIEWBOX_WIDTH = 300;

/**
 * SVG viewBox height for the chart line.
 */
const CHART_VIEWBOX_HEIGHT = 200;

/**
 * Height (px) of each ticker row.
 */
const TICKER_HEIGHT = 32;

/**
 * Example goals shown in the ticker. The order must match CHART_LINES.
 */
const GOAL_NAMES = [
  'Define your goal',
  'Drive Purchase',
  'Increase email engagement',
  'Reduce cart abandonment',
];

/**
 * Chart line SVG points for each goal. Each entry corresponds to a goal in GOAL_NAMES by index.
 * The points are [x, y] pairs, with y=0 at the top of the SVG.
 */
const CHART_LINES: [number, number][][] = [
  // 1. Gentle wavy line, ending 30% higher for 'Define your goal'
  [
    [0, 100],
    [30, 97],
    [60, 103],
    [90, 98],
    [120, 102],
    [150, 100],
    [180, 97],
    [210, 103],
    [240, 98],
    [270, 90],
    [300, 70], // 30% higher (y=70)
  ],
  // 2. Strong diagonal up for 'Drive Purchase'
  [
    [0, 180],
    [30, 160],
    [60, 140],
    [90, 120],
    [120, 100],
    [150, 80],
    [180, 60],
    [210, 50],
    [240, 40],
    [270, 30],
    [300, 20],
  ],
  // 3. Down then up for 'Increase email engagement'
  [
    [0, 20],
    [30, 40],
    [60, 60],
    [90, 80],
    [120, 100],
    [150, 120],
    [180, 140],
    [210, 120],
    [240, 80],
    [270, 40],
    [300, 20],
  ],
  // 4. Strong diagonal down for 'Reduce cart abandonment'
  [
    [0, 20],
    [30, 40],
    [60, 60],
    [90, 80],
    [120, 100],
    [150, 120],
    [180, 140],
    [210, 150],
    [240, 160],
    [270, 170],
    [300, 180],
  ],
];

// Animated border gradient keyframes
const borderGradientMove = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 100% 50%;
  }
`;

const Card = styled.div`
  position: relative;
  overflow: hidden;
  z-index: 0;
  width: ${pxToRem(337)};
  padding: ${spacing.plusHalf};
  font-family: inherit, sans-serif;
  color: ${colors.textSecondary};
  border-radius: ${pxToRem(12)};
  background: rgba(255, 255, 255, 0.75);
  box-shadow:
    0px 4px 8px 0px rgba(0, 0, 0, 0.04),
    0px 0px 2px 0px rgba(0, 0, 0, 0.06),
    0px 0px 1px 0px rgba(0, 0, 0, 0.12);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: inherit;
    padding: 1.5px;
    background: linear-gradient(
      240deg,
      ${colors.mustard20},
      rgba(255, 255, 255, 1),
      ${colors.mustard20}
    );
    background-size: 200% 200%;
    animation: ${borderGradientMove} 4s linear infinite;
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
`;

const GoalTickerContainer = styled.div`
  height: ${pxToRem(TICKER_HEIGHT)};
  overflow: hidden;
  margin-bottom: ${spacing.half};
`;

const GoalTicker = styled.div<{ $offset: number; $transition: boolean }>`
  display: flex;
  flex-direction: column;
  transform: translateY(-${(props) => pxToRem(props.$offset * 32)});
  ${(props) =>
    props.$transition &&
    css`
      transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    `}
`;

const GoalLine = styled.div`
  ${body1Medium}
  color: ${colors.textDisabled};
  line-height: ${pxToRem(TICKER_HEIGHT)};
  height: ${pxToRem(TICKER_HEIGHT)};
`;

const Subtitle = styled.div`
  ${body2}
  color: ${colors.textDisabled};
  margin-bottom: ${spacing.quarter};
`;

const Stats = styled.div`
  margin-bottom: ${spacing.plusHalf};
`;

const Big = styled.span`
  color: ${colors.textDisabled};
  font-size: ${pxToRem(30)};
  font-family: ${fonts.sansSerif};
  font-weight: ${fontWeights.bold};
  line-height: ${fontSizes.s28};
  letter-spacing: ${letterSpacing.neg25};
  font-style: normal;
  font-variant-numeric: lining-nums tabular-nums;
  margin-right: ${spacing.half};
`;

const Green = styled.span`
  ${body1Bold}
  color: ${colors.textSuccess};
  font-variant-numeric: lining-nums tabular-nums;
  margin-right: ${spacing.half};
`;

const Muted = styled.span`
  ${subtitle2}
  color: ${colors.textDisabled};
`;

const ChartLineWrapper = styled.div`
  width: 100%;
  height: ${pxToRem(96)};
  margin-bottom: ${spacing.half};
  display: flex;
  align-items: flex-end;
  position: relative;
`;

const FadeOverlay = styled.div`
  inset: 0;
  pointer-events: none;
  position: absolute;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0) 10%,
    rgba(255, 255, 255, 0) 90%,
    rgba(255, 255, 255, 1) 100%
  );
  height: ${pxToRem(96)};
  width: 100%;
  z-index: 1;
`;

const lineDraw = keyframes`
  to {
    stroke-dashoffset: 0;
  }
`;

const AnimatedLine = styled.polyline<{ $animate: boolean }>`
  fill: none;
  stroke: #d3cecf;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 1000;
  stroke-dashoffset: 1000;
  ${(props) =>
    props.$animate &&
    css`
      animation: ${lineDraw} 3.6s cubic-bezier(0.65, 0, 0.35, 1) forwards;
    `}
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #eee;
  padding-top: 16px;
  font-size: 15px;
`;

const FooterLabel = styled.span`
  ${body2}
  color: ${colors.textSecondary};
`;

const FooterValue = styled.span`
  ${caption}
  color: ${colors.textSecondary};
`;

interface ChartLineProps {
  points: [number, number][];
  animate: boolean;
}

const ChartLine = ({ points, animate }: ChartLineProps) => {
  const pointsStr = useMemo(() => points.map(([x, y]) => `${x},${y}`).join(' '), [points]);
  return (
    <svg
      width="100%"
      height="96"
      viewBox={`0 0 ${CHART_VIEWBOX_WIDTH} ${CHART_VIEWBOX_HEIGHT}`}
      style={{ overflow: 'visible', display: 'block' }}
      preserveAspectRatio="none"
    >
      <AnimatedLine points={pointsStr} $animate={animate} />
    </svg>
  );
};

type AnimationState = 'ticker' | 'line' | 'idle';

/**
 * Custom hook for a seamless, always-forward infinite ticker with animation state machine.
 * Handles ticker index, goal index, and transition state, including the rAF jump logic.
 */
function useInfiniteTicker(goalCount: number) {
  const pendingReset = useRef(false);

  const [tickerIndex, setTickerIndex] = useState(0);
  const [activeGoalIndex, setActiveGoalIndex] = useState(0);
  const [isTickerTransitioning, setIsTickerTransitioning] = useState(true);
  const [animationState, setAnimationState] = useState<AnimationState>('ticker');

  // Advance ticker and animation state machine
  const advance = useCallback(() => {
    if (tickerIndex === goalCount - 1) {
      setTickerIndex(tickerIndex + 1); // animate to extra copy
      // Don't update activeGoalIndex yet
    } else if (tickerIndex < goalCount - 1) {
      setTickerIndex(tickerIndex + 1);
      setActiveGoalIndex((i) => (i + 1) % goalCount);
    }
  }, [tickerIndex, goalCount]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (animationState === 'ticker') {
      timeout = setTimeout(() => setAnimationState('line'), TICKER_TRANSITION_MS);
    } else if (animationState === 'line') {
      timeout = setTimeout(() => setAnimationState('idle'), LINE_ANIMATION_MS);
    } else if (animationState === 'idle') {
      timeout = setTimeout(() => {
        advance();
        setAnimationState('ticker');
      }, IDLE_DELAY_MS);
    }
    return () => clearTimeout(timeout);
  }, [animationState, tickerIndex, advance, setAnimationState]);

  /**
   * On ticker transition end, if at the extra copy, jump to 0 with no transition.
   * Use rAF to ensure browser paints before resetting, then re-enable transition.
   */
  const handleTransitionEnd = useCallback(() => {
    if (tickerIndex === goalCount) {
      if (!pendingReset.current) {
        pendingReset.current = true;
        setIsTickerTransitioning(false);
        requestAnimationFrame(() => {
          setTickerIndex(0);
          setActiveGoalIndex(0);
          requestAnimationFrame(() => {
            setIsTickerTransitioning(true);
            pendingReset.current = false;
          });
        });
      }
    }
  }, [tickerIndex, goalCount]);

  return {
    tickerIndex,
    activeGoalIndex,
    isTickerTransitioning,
    animationState,
    handleTransitionEnd,
  };
}

export const EmptyStateExampleCard = (props: HTMLAttributes<HTMLDivElement>) => {
  const tickerGoals = useMemo(() => [...GOAL_NAMES, GOAL_NAMES[0]], []);
  const {
    tickerIndex,
    activeGoalIndex,
    isTickerTransitioning,
    animationState,
    handleTransitionEnd,
  } = useInfiniteTicker(GOAL_NAMES.length);
  const chartPoints = useMemo(() => CHART_LINES[activeGoalIndex], [activeGoalIndex]);

  return (
    <Card {...props}>
      <GoalTickerContainer aria-live="polite">
        <GoalTicker
          $offset={tickerIndex}
          $transition={isTickerTransitioning}
          onTransitionEnd={handleTransitionEnd}
        >
          {tickerGoals.map((goal, idx) => (
            <GoalLine
              key={goal + idx}
              role={idx === activeGoalIndex ? 'status' : undefined}
              aria-atomic={idx === activeGoalIndex ? 'true' : undefined}
            >
              {goal}
            </GoalLine>
          ))}
        </GoalTicker>
      </GoalTickerContainer>
      <Subtitle>Total conversions</Subtitle>
      <Stats>
        <Big>10,000</Big>
        <Green>+X.X%</Green>
        <Muted>from last 7 days</Muted>
      </Stats>
      <ChartLineWrapper>
        <ChartLine points={chartPoints} animate={animationState === 'line'} />
        <FadeOverlay />
      </ChartLineWrapper>
      <Footer>
        <FooterLabel>Top campaign</FooterLabel>
        <FooterValue>Out of 20 campaigns</FooterValue>
      </Footer>
    </Card>
  );
};
